import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useStepRedirect, skipNextStepRedirect } from "../../../hooks/useStepRedirect";
import { useProgressWithAutoRefresh, triggerProgressRefresh } from "../../../shared/contexts/ProgressContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import LocationPicker from "../../../shared/components/ui/LocationPicker";
import { ClosureApiService, CloseExistingClinicRequest } from "../../closure/api/ClosureApiService";
import { normalizeNumericInput } from "../../../shared/utils/numberUtils";
import CitySelect from "../../../shared/components/ui/CitySelect";
import { useUnsavedFormGuard } from "../../../shared/utils/unsavedChanges";

/** Iraqi mobile: 07XXXXXXXXX, 7XXXXXXXXX or +964 7XXXXXXXXX (75–79), as in the new design. */
const isIraqiMobile = (value: string): boolean => {
  const local = value.replace(/\D/g, "").replace(/^964/, "").replace(/^0+/, "");
  return /^7[5-9]\d{8}$/.test(local);
};

// Governorate mapping (same as ClinicInfoScreen)
const GOVERNORATE_API_MAP: Record<string, string> = {
  Baghdad: "baghdad",
  Basrah: "basra",
  Nineveh: "nineveh",
  Maysan: "maysan",
  Diyala: "diyala",
  "Al Anbar": "anbar",
  Kirkuk: "kirkuk",
  Saladin: "saladin",
  Duhok: "dohuk",
  Erbil: "erbil",
  Sulaymaniyah: "sulaymaniyah",
  Halabja: "halabja",
  Najaf: "najaf",
  Karbala: "karbala",
  Babylon: "babil",
  Wasit: "wasit",
  "Al Qadisiyah": "qadisiyah",
  "Al Muthanna": "muthanna",
  Dhiqar: "dhi_qar",
};

const GOVERNORATES = Object.keys(GOVERNORATE_API_MAP);

const CLOSURE_TYPES = ["permanent", "temporary", "seasonal"] as const;

interface ClosureFormData {
  // Clinic details
  name: string;
  governorate: string;
  area_id: string;
  neighborhood: string;
  street: string;
  house_number: string;
  nearest_landmark: string;
  latitude: string | null;
  longitude: string | null;
  location_address: string | null;
  phone_primary: string;
  phone_secondary: string;
  // Closure details
  closure_reason: string;
  closure_date: string;
  temporary_closure_end_date: string;
}

const INITIAL_FORM_DATA: ClosureFormData = {
  name: "",
  governorate: "",
  area_id: "",
  neighborhood: "",
  street: "",
  house_number: "",
  nearest_landmark: "",
  latitude: null,
  longitude: null,
  location_address: null,
  phone_primary: "",
  phone_secondary: "",
  closure_reason: "",
  closure_date: "",
  temporary_closure_end_date: "",
};

const REQUIRED_FIELDS: Array<keyof ClosureFormData> = [
  "name",
  "governorate",
  "area_id",
  "neighborhood",
  "street",
  "house_number",
  "nearest_landmark",
  "phone_primary",
];

const GOVERNORATE_REVERSE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(GOVERNORATE_API_MAP).map(([display, api]) => [api, display])
);

const ExistingClinicClosureScreen: React.FC = () => {
  useStepRedirect();

  const [formData, setFormData] = useState<ClosureFormData>(INITIAL_FORM_DATA);
  // Ask before a design switch discards typed changes (both designs share this guard).
  useUnsavedFormGuard(formData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const hasLoadedData = useRef(false);

  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { progress, isLoading: progressLoading } = useProgressWithAutoRefresh();
  const closureApi = useMemo(() => new ClosureApiService(), []);

  const fromReview = !!(location.state as any)?.fromReview;

  // Pre-fill form data from existing closure request
  const closureRequest = progress?.active_requests?.find(
    (r: any) => r.request_type === "closure"
  );
  const showCancelInsteadOfBack =
    !fromReview && !!closureRequest && closureRequest.status === "draft";

  useEffect(() => {
    if (hasLoadedData.current || !progress || !closureRequest) return;
    hasLoadedData.current = true;

    const requestId = closureRequest.request_id;

    const loadData = async () => {
      try {
        const result = await closureApi.getClosureRequest(requestId);
        if (result.isSuccess && result.data) {
          const data = result.data as any;
          const clinic = data.clinic || {};
          setFormData({
            name: data.name || clinic.name || "",
            governorate: GOVERNORATE_REVERSE_MAP[data.governorate || clinic.governorate] || data.governorate || clinic.governorate || "",
            area_id: data.area?.id || clinic.area?.id || clinic.area_id || "",
            neighborhood: (data.neighborhood ?? clinic.neighborhood)?.toString() || "",
            street: (data.street ?? clinic.street)?.toString() || "",
            house_number: (data.house_number ?? clinic.house_number)?.toString() || "",
            nearest_landmark: data.nearest_landmark || clinic.nearest_landmark || "",
            latitude: (data.latitude ?? clinic.latitude)?.toString() || null,
            longitude: (data.longitude ?? clinic.longitude)?.toString() || null,
            location_address: data.location_address || clinic.location_address || null,
            phone_primary: data.phone_primary || clinic.phone_primary || "",
            phone_secondary: data.phone_secondary || clinic.phone_secondary || "",
            closure_reason: data.closure_reason || "",
            closure_date: data.closure_date || "",
            temporary_closure_end_date: data.temporary_closure_end_date || "",
          });
        }
      } catch (error) {
        console.error("[ExistingClinicClosureScreen] Failed to load existing data:", error);
      }
    };

    loadData();
  }, [closureRequest, progress, closureApi]);

  const handleInputChange = <Field extends keyof ClosureFormData>(
    field: Field,
    value: ClosureFormData[Field]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => ({ ...prev, [field as string]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Check required fields
    REQUIRED_FIELDS.forEach((field) => {
      const value = formData[field];
      if (typeof value === "string" && !value.trim()) {
        newErrors[field as string] = t("field_required") || "This field is required";
      }
    });

    // Validate governorate
    if (formData.governorate && !GOVERNORATES.includes(formData.governorate)) {
      newErrors.governorate = t("invalid_governorate") || "Invalid governorate";
    }

    // Validate phone numbers: Iraqi mobile, the same rule as the new design (closureShared IRAQI_MOBILE)
    if (formData.phone_primary && !isIraqiMobile(formData.phone_primary)) {
      newErrors.phone_primary = t("invalid_phone_number") || "Invalid phone number";
    }
    if (formData.phone_secondary.trim() && !isIraqiMobile(formData.phone_secondary)) {
      newErrors.phone_secondary = t("invalid_phone_number") || "Invalid phone number";
    }

    // Location is required
    if (!formData.latitude || !formData.longitude) {
      newErrors.location = t("field_required") || "This field is required";
    }

    // Validate dates if provided - must not be in the past
    if (formData.closure_date) {
      const date = new Date(formData.closure_date);
      if (isNaN(date.getTime())) {
        newErrors.closure_date = t("invalid_date") || "Invalid date";
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) {
          newErrors.closure_date = t("date_cannot_be_in_past") || "Date cannot be in the past";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmClosure = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      const payload: CloseExistingClinicRequest = {
        name: formData.name.trim(),
        governorate: GOVERNORATE_API_MAP[formData.governorate] || formData.governorate,
        area_id: formData.area_id,
        phone_primary: formData.phone_primary.trim(),
      };

      // Add optional clinic fields
      if (formData.neighborhood) {
        payload.neighborhood = parseInt(formData.neighborhood, 10);
      }
      if (formData.street) {
        payload.street = parseInt(formData.street, 10);
      }
      if (formData.house_number) {
        payload.house_number = parseInt(formData.house_number, 10);
      }
      if (formData.nearest_landmark.trim()) {
        payload.nearest_landmark = formData.nearest_landmark.trim();
      }
      if (formData.latitude) {
        payload.latitude = parseFloat(formData.latitude);
      }
      if (formData.longitude) {
        payload.longitude = parseFloat(formData.longitude);
      }
      if (formData.phone_secondary.trim()) {
        payload.phone_secondary = formData.phone_secondary.trim();
      }

      if (formData.closure_reason.trim()) {
        payload.closure_reason = formData.closure_reason.trim();
      }
      if (formData.closure_date) {
        payload.closure_date = formData.closure_date;
      }

      let result;
      if (closureRequest?.request_id) {
        result = await closureApi.updateExistingClosure(closureRequest?.request_id, payload);
      } else {
        result = await closureApi.closeExistingClinic(payload);
      }

      if (result.isSuccess && result.data) {
        skipNextStepRedirect();
        if (fromReview) {
          navigate("/closure-review");
        } else {
          navigate("/closure-documents", {
            state: {
              requestId: result.data.id,
              clinicName: formData.name,
              isExistingClinic: true,
            },
          });
        }
      } else {
        setErrors({ submit: result.error || t("failed_to_create_closure_request") || "Failed to create closure request" });
      }
    } catch (error) {
      setErrors({
        submit: error instanceof Error ? error.message : t("failed_to_create_closure_request") || "Failed to create closure request",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    skipNextStepRedirect();
    navigate(fromReview ? "/closure-review" : "/home");
  };

  const handleCancelRequest = async () => {
    if (!closureRequest?.request_id) return;
    setShowCancelDialog(false);
    setCancelling(true);
    try {
      const result = await closureApi.cancelClosureRequest(closureRequest.request_id);
      if (result.isSuccess) {
        triggerProgressRefresh();
        navigate("/home", { replace: true });
      } else {
        setErrors({ submit: result.error || t("failed_to_cancel_request") || "Failed to cancel request" });
      }
    } catch (error) {
      setErrors({
        submit: error instanceof Error ? error.message : t("failed_to_cancel_request") || "Failed to cancel request",
      });
    } finally {
      setCancelling(false);
    }
  };

  const continueButton = (
    <div className="flex gap-3 w-full">
      <button
        onClick={showCancelInsteadOfBack ? () => setShowCancelDialog(true) : handleBack}
        disabled={loading || cancelling}
        className={`min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold transition-all duration-200 ${
          showCancelInsteadOfBack
            ? "border border-red-300 text-red-600 hover:bg-red-50"
            : "border border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
      >
        {showCancelInsteadOfBack ? (t("cancel_request") || "Cancel Request") : (t("back") || "Back")}
      </button>
      <button
        onClick={handleSubmit}
        disabled={loading}
        className={`flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
          loading
            ? "opacity-50 cursor-not-allowed"
            : "hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg"
        }`}
        style={{ backgroundColor: "#c03744" }}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            <span>{t("loading") || "Loading..."}</span>
          </div>
        ) : (
          <span>{closureRequest?.request_id ? (t("save_changes") || "Save Changes") : (t("request_closure") || "Request Closure")}</span>
        )}
      </button>
    </div>
  );

  return (
    <ResponsiveFormLayout
      onBack={showCancelInsteadOfBack ? () => setShowCancelDialog(true) : handleBack}
      showBackButton={true}
      showLanguageToggle={true}
      icon="❌"
      iconSize="w-24 h-24 text-6xl"
      title={t("close_existing_clinic") || "Close Existing Clinic"}
      subtitle={t("close_existing_clinic_subtitle") || "For clinics not registered in the system"}
      actions={continueButton}
      maxHeight="90vh"
    >
      <div className="w-full space-y-6">
        {/* Error Message */}
        {errors.submit && (
          <div className="w-full rounded-xl p-4 bg-red-50 border border-red-200">
            <div className="flex items-start gap-3">
              <span className="text-red-500 text-lg">⚠️</span>
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          </div>
        )}

        {/* Info Message */}
        <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
          <div className="flex items-start gap-3">
            <span className="text-blue-500 text-2xl">ℹ️</span>
            <div>
              <h4 className="font-semibold text-blue-800 mb-1">
                {t("existing_clinic_info_title") || "Existing Clinic Closure"}
              </h4>
              <p className="text-sm text-blue-700">
                {t("existing_clinic_info_message") ||
                  "Use this form if you have a clinic that was never registered through this system and you want to close it."}
              </p>
            </div>
          </div>
        </div>

        {/* Clinic Details Section */}
        <section className="space-y-4">
          <h3 className={`text-base font-semibold text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
            {t("clinic_details") || "Clinic Details"}
          </h3>

          {/* Clinic Name */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
              {t("clinic_name") || "Clinic Name"} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder={t("enter_clinic_name") || "Enter clinic name"}
              className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                errors.name
                  ? "border-red-500"
                  : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
              }`}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Governorate & City */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
                {t("governorate") || "Governorate"} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.governorate}
                onChange={(e) => {
                  handleInputChange("governorate", e.target.value);
                  handleInputChange("area_id", "");
                }}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.governorate
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              >
                <option value="">{t("select_governorate") || "Select Governorate"}</option>
                {GOVERNORATES.map((governorate) => (
                  <option key={governorate} value={governorate}>
                    {t(governorate) || governorate}
                  </option>
                ))}
              </select>
              {errors.governorate && <p className="text-red-500 text-sm mt-1">{errors.governorate}</p>}
            </div>

            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
                {t("city") || "City"} <span className="text-red-500">*</span>
              </label>
              <CitySelect
                value={formData.area_id}
                governorate={GOVERNORATE_API_MAP[formData.governorate] || formData.governorate}
                onChange={(id) => handleInputChange("area_id", id)}
                isRTL={isRTL}
                disabled={!formData.governorate}
                error={errors.area_id}
                placeholder={t("select_city") || "Select City"}
                loadingText={t("loading") || "Loading..."}
              />
              {errors.area_id && <p className="text-red-500 text-sm mt-1">{errors.area_id}</p>}
            </div>
          </div>

          {/* Address Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
                {t("neighborhood") || "Neighborhood"} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.neighborhood}
                onChange={(e) => handleInputChange("neighborhood", normalizeNumericInput(e.target.value))}
                placeholder={t("neighborhood_placeholder") || "Neighborhood number"}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.neighborhood
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              />
              {errors.neighborhood && <p className="text-red-500 text-sm mt-1">{errors.neighborhood}</p>}
            </div>

            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
                {t("street") || "Street"} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.street}
                onChange={(e) => handleInputChange("street", normalizeNumericInput(e.target.value))}
                placeholder={t("street_placeholder") || "Street number"}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.street
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              />
              {errors.street && <p className="text-red-500 text-sm mt-1">{errors.street}</p>}
            </div>

            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
                {t("house_number") || "House Number"} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.house_number}
                onChange={(e) => handleInputChange("house_number", normalizeNumericInput(e.target.value))}
                placeholder={t("house_number_placeholder") || "House number"}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.house_number
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              />
              {errors.house_number && <p className="text-red-500 text-sm mt-1">{errors.house_number}</p>}
            </div>
          </div>

          {/* Nearest Landmark */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
              {t("nearest_landmark") || "Nearest Landmark"} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nearest_landmark}
              onChange={(e) => handleInputChange("nearest_landmark", e.target.value)}
              placeholder={t("enter_nearest_landmark") || "Enter nearest landmark"}
              className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                errors.nearest_landmark
                  ? "border-red-500"
                  : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
              }`}
            />
            {errors.nearest_landmark && <p className="text-red-500 text-sm mt-1">{errors.nearest_landmark}</p>}
          </div>

          {/* Location Picker */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
              {t("clinic_location") || "Clinic Location"} <span className="text-red-500">*</span>
            </label>
            {formData.latitude && formData.longitude ? (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-sm text-green-800 font-medium mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t("location_selected") || "Location Selected"}
                      </div>
                      {formData.location_address ? (
                        <div className="text-sm text-gray-800">
                          <p className="font-medium mb-1">{formData.location_address}</p>
                          <p className="text-xs text-gray-500 font-mono">
                            {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                          </p>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600 font-mono">
                          {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          latitude: null,
                          longitude: null,
                          location_address: null,
                        }));
                      }}
                      className="text-red-500 hover:text-red-700 transition-colors p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsLocationPickerOpen(true)}
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>{t("change_location") || "Change Location"}</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsLocationPickerOpen(true)}
                className={`w-full px-4 py-3 border-2 border-dashed rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600 ${
                  errors.location ? "border-red-500" : "border-gray-300"
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{t("select_location_on_map") || "Select Location on Map"}</span>
              </button>
            )}
            <LocationPicker
              value={formData.latitude && formData.longitude ? {
                lat: formData.latitude,
                lng: formData.longitude,
                address: formData.location_address || undefined
              } : null}
              onChange={(location) => {
                if (location) {
                  setFormData((prev) => ({
                    ...prev,
                    latitude: location.lat,
                    longitude: location.lng,
                    location_address: location.address || null,
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    latitude: null,
                    longitude: null,
                    location_address: null,
                  }));
                }
              }}
              isOpen={isLocationPickerOpen}
              onClose={() => setIsLocationPickerOpen(false)}
              governorate={formData.governorate}
            />
            {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
          </div>

          {/* Phone Numbers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
                {t("phone_primary") || "Primary Phone"} <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone_primary}
                onChange={(e) => handleInputChange("phone_primary", e.target.value)}
                placeholder={t("enter_phone_number") || "Enter phone number"}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.phone_primary
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              />
              {errors.phone_primary && <p className="text-red-500 text-sm mt-1">{errors.phone_primary}</p>}
              <p className="text-xs text-gray-500 mt-1">{t("iraqi_phone_hint")}</p>
            </div>

            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
                {t("phone_secondary") || "Secondary Phone"}
              </label>
              <input
                type="tel"
                value={formData.phone_secondary}
                onChange={(e) => handleInputChange("phone_secondary", e.target.value)}
                placeholder={t("enter_phone_number") || "Enter phone number"}
                className={`w-full px-4 py-3 border rounded-xl focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20 ${
                  errors.phone_secondary ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.phone_secondary && <p className="text-red-500 text-sm mt-1">{errors.phone_secondary}</p>}
            </div>
          </div>
        </section>

        {/* Closure Details Section */}
        <section className="space-y-4 pt-4 border-t border-gray-200">
          <h3 className={`text-base font-semibold text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
            {t("closure_details") || "Closure Details"}
          </h3>

          {/* Closure Reason */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
              {t("closure_reason") || "Reason for Closure"}
            </label>
            <textarea
              value={formData.closure_reason}
              onChange={(e) => handleInputChange("closure_reason", e.target.value)}
              placeholder={t("enter_closure_reason") || "Enter the reason for closing your clinic..."}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
            />
          </div>

          {/* Closure Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
                {t("closure_date") || "Closure Date"}
              </label>
              <input
                type="date"
                value={formData.closure_date}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => handleInputChange("closure_date", e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.closure_date
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              />
              {errors.closure_date && <p className="text-red-500 text-sm mt-1">{errors.closure_date}</p>}
            </div>
          </div>
        </section>
      </div>

      {/* Cancel Request Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <span className="text-5xl">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
              {t("confirm_cancel_request") || "Cancel Request?"}
            </h3>
            <p className="text-gray-600 mb-6 text-sm text-center">
              {t("cancel_request_message") || "Are you sure you want to cancel this request? This action cannot be undone."}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelDialog(false)}
                disabled={cancelling}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all"
              >
                {t("no_keep_it") || "No, Keep It"}
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={cancelling}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-all flex items-center justify-center gap-2"
              >
                {cancelling && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                <span>{t("yes_cancel_it") || "Yes, Cancel It"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <span className="text-5xl">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
              {closureRequest?.request_id
                ? (t("confirm_update_request") || "Confirm Update")
                : (t("confirm_closure_request") || "Confirm Closure Request")}
            </h3>
            <p className="text-gray-600 mb-6 text-sm text-center">
              {closureRequest?.request_id
                ? (t("confirm_update_closure_message") || "Are you sure you want to update your closure request?")
                : (t("confirm_existing_closure_message") || "Are you sure you want to submit a closure request for this clinic? You will need to upload required documents after this step.")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all"
              >
                {t("cancel") || "Cancel"}
              </button>
              <button
                onClick={handleConfirmClosure}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-all"
              >
                {t("confirm") || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ResponsiveFormLayout>
  );
};

export default ExistingClinicClosureScreen;
